import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { PostPreview } from "@/components/PostPreview";
import { Comments } from "@/components/Comments";
import { ClientCalendar } from "@/components/ClientCalendar";
import { ArrowLeft, Check, MessageSquareWarning } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { statusMeta } from "@/lib/constants";
import { StatusDot } from "@/components/StatusDot";
import { MONTHS } from "@/lib/constants";
import { todayMonth } from "@/lib/format";

export default function ClientPost() {
  const { token, postId } = useParams();
  const navigate = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [posts, setPosts] = useState([]);
  const [activeId, setActiveId] = useState(postId);
  const [post, setPost] = useState(null);
  const [videoSecond, setVideoSecond] = useState(0);
  const [requesting, setRequesting] = useState(false);
  const [reason, setReason] = useState("");
  const [approved, setApproved] = useState(false);
  const [error, setError] = useState("");
  const videoElRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const home = await api.get(`/c/${token}`);
        setCtx(home.data);
        document.documentElement.style.setProperty("--brand-color", home.data.brand?.color || "#4A5568");
        const list = await api.get(`/c/${token}/posts`);
        setPosts(list.data);
        const mo = list.data[0]?.date?.slice(0, 7) || todayMonth();
        const mn = Number(mo.split("-")[1]);
        document.title = `Calendario del mes de ${MONTHS[mn - 1]} de ${home.data.brand?.name}`;
      } catch (e) { setError(e.response?.data?.detail || "No encuentro este contenido."); }
    })();
  }, [token]);

  useEffect(() => {
    if (!activeId) return;
    setPost(null);
    setRequesting(false);
    setReason("");
    api.get(`/c/${token}/posts/${activeId}`).then((r) => setPost(r.data)).catch((e) => setError(e.response?.data?.detail || "No encuentro este post."));
    navigate(`/c/${token}/post/${activeId}`, { replace: true });
    /* eslint-disable-next-line */
  }, [activeId]);

  const refreshList = () => api.get(`/c/${token}/posts`).then((r) => setPosts(r.data));
  const reloadActive = () => api.get(`/c/${token}/posts/${activeId}`).then((r) => setPost(r.data));
  const gotoNext = () => {
    const i = posts.findIndex((p) => p.id === activeId);
    if (i >= 0 && i < posts.length - 1) setActiveId(posts[i + 1].id);
    else reloadActive();
  };

  const approve = async () => {
    await api.post(`/c/${token}/posts/${activeId}/approve`);
    setApproved(true);
    setTimeout(() => { setApproved(false); toast.success("Aprobado"); refreshList(); gotoNext(); }, 1100);
  };
  const requestChanges = async () => {
    if (!reason.trim()) { toast.error("Escribe un motivo para que el estudio sepa qué cambiar."); return; }
    try {
      await api.post(`/c/${token}/posts/${activeId}/request-changes`, { reason });
      toast.success("Cambios solicitados");
      setRequesting(false); setReason("");
      refreshList(); gotoNext();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onVideoTime = (t, el) => { setVideoSecond(t); videoElRef.current = el; };

  if (error) return <div className="min-h-screen bg-surface flex items-center justify-center p-6 text-center"><p className="text-subtle text-sm">{error}</p></div>;
  if (!ctx) return <div className="min-h-screen bg-surface flex items-center justify-center text-subtle">Cargando…</div>;

  const st = post ? statusMeta(post.status) : null;
  const canDecide = post?.status === "enviado_cliente";
  const isVideo = post && (post.type === "video" || post.files?.some((f) => f.type === "video"));

  return (
    <div className="min-h-screen bg-surface pb-16">
      <header className="sticky top-0 z-30 bg-surface/95 backdrop-blur border-b border-line">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 h-16 flex items-center gap-3">
          <button onClick={() => navigate(`/c/${token}${post?.date ? `?m=${post.date.slice(0, 7)}` : ""}`)} data-testid="client-back" className="w-9 h-9 rounded-full border border-line flex items-center justify-center hover:bg-app"><ArrowLeft size={17} /></button>
          <div className="w-8 h-8 rounded-full overflow-hidden border border-line bg-app flex items-center justify-center shrink-0">
            {ctx.brand?.logo_url ? <img src={fileUrl(ctx.brand.logo_url)} alt="" className="w-full h-full object-cover" /> : <span className="font-display text-sm" style={{ color: ctx.brand?.color }}>{ctx.brand?.name?.[0]}</span>}
          </div>
          <span className="font-medium text-sm">{ctx.brand?.name}</span>
          {st && <span className="ml-auto flex items-center gap-1.5 text-xs text-subtle"><StatusDot color={st.dot} /> {st.label}</span>}
        </div>
      </header>

      <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 py-8">
        <div className="grid justify-center gap-10 xl:gap-16 lg:grid-cols-[440px_minmax(0,560px)] items-start">
          {/* Left: preview + actions */}
          <div className="w-full max-w-[440px] mx-auto lg:mx-0">
            {post ? (
              <PostPreview key={activeId} post={post} brand={ctx.brand} onVideoTime={onVideoTime} rounded />
            ) : (
              <div className="w-full aspect-square rounded-3xl bg-app border border-line animate-pulse" />
            )}

            <div className="mt-5">
              {post && (canDecide ? (
                requesting ? (
                  <div className="space-y-3">
                    <textarea autoFocus value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="¿Qué habría que cambiar?" data-testid="reason-input" className="w-full border border-line rounded-2xl px-4 py-3 text-sm resize-none bg-surface focus:outline-none" />
                    <div className="flex gap-3">
                      <button onClick={() => setRequesting(false)} className="flex-1 py-3 border border-line rounded-full text-sm">Cancelar</button>
                      <button onClick={requestChanges} data-testid="submit-changes" className="flex-1 py-3 rounded-full text-white text-sm bg-changes">Enviar solicitud</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={() => setRequesting(true)} data-testid="request-changes-btn" className="flex-1 py-3 border border-line rounded-full text-sm flex items-center justify-center gap-2"><MessageSquareWarning size={16} /> Pedir cambios</button>
                    <button onClick={approve} data-testid="approve-btn" className="flex-1 py-3 rounded-full text-white text-sm flex items-center justify-center gap-2" style={{ background: "var(--brand-color)" }}><Check size={16} /> Aprobar</button>
                  </div>
                )
              ) : (
                <div className="flex items-center gap-2 text-sm text-subtle rounded-2xl border border-line px-4 py-3"><StatusDot color={st.dot} /> Ya revisado · {st.label}</div>
              ))}
            </div>
          </div>

          {/* Right: calendar picker */}
          <div className="w-full lg:sticky lg:top-24">
            <h2 className="font-display text-lg mb-3">Elige un día</h2>
            <ClientCalendar posts={posts} activeId={activeId} initialMonth={posts.find((p) => p.id === activeId)?.date?.slice(0, 7)} onSelect={(id) => setActiveId(id)} />
          </div>
        </div>

        {/* Comments centered below */}
        {post && (
          <div className="max-w-[560px] mx-auto mt-12">
            <h2 className="font-display text-lg mb-3">Comentarios</h2>
            <div className="min-h-[200px]">
              <Comments postId={activeId} isVideo={isVideo} videoSecond={videoSecond} mode="client" token={token} initialComments={post.comments} onSeek={(s) => { if (videoElRef.current) { videoElRef.current.currentTime = s; videoElRef.current.play()?.catch(() => {}); } }} />
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {approved && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-surface/95 z-50 flex flex-col items-center justify-center" data-testid="approved-overlay">
            <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }} className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--brand-color)" }}>
              <Check size={32} className="text-white" />
            </motion.div>
            <p className="font-display text-3xl mt-5">Aprobado</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
